/**
 * Som e música (PRD §6).
 *
 * ## Sintetizado, e não gravado
 *
 * Nenhum arquivo de áudio entra no repositório, e a razão é a mesma que rege a arte: tudo
 * aqui é desenhado em código (ver `art/`). Um `.ogg` de dois segundos é um binário que
 * ninguém revisa no diff, que não se ajusta sem uma ferramenta externa, e que multiplica o
 * peso do bundle por cada variação. O `AudioContext` do navegador é um sintetizador
 * completo — osciladores, ruído, filtros, envelopes — e é com ele que os sons são feitos,
 * do mesmo jeito que os sprites são feitos com `canvas`.
 *
 * O custo é honesto: som sintetizado soa sintetizado. É a mesma troca que a arte faz, e
 * pelo mesmo motivo — o jogo precisa de **feedback**, e feedback imediato e coerente vale
 * mais que timbre bonito. Trocar por amostras depois não muda nada além deste arquivo.
 *
 * ## O contexto só nasce depois de um clique
 *
 * Todo navegador moderno recusa criar (ou manter) um `AudioContext` antes de um gesto do
 * usuário. Criá-lo na carga da página produz um contexto suspenso que nunca toca nada e um
 * aviso no console — e o jogo já tem um gesto natural para isso: o botão de entrar. Aqui o
 * contexto é criado preguiçosamente no primeiro som pedido, e `resume()` é chamado sempre,
 * porque a aba pode ter sido suspensa no meio da sessão.
 */

export const Sfx = {
  Step: 'step',
  Hit: 'hit',
  Miss: 'miss',
  Death: 'death',
  Fire: 'fire',
  Ice: 'ice',
  Energy: 'energy',
  Heal: 'heal',
  Advance: 'advance',
  Loss: 'loss',
  Coin: 'coin',
  Ui: 'ui',
} as const;

export type Sfx = (typeof Sfx)[keyof typeof Sfx];

/** Volume guardado entre sessões: mexer nele toda vez que se entra é atrito puro. */
const STORAGE_KEY = 'hexaria:audio';

interface Settings {
  master: number;
  music: number;
}

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { master: 0.6, music: 0.35 };

    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      master: clamp(parsed.master ?? 0.6),
      music: clamp(parsed.music ?? 0.35),
    };
  } catch {
    // `localStorage` pode estar bloqueado (modo privado, política de site). Áudio não é
    // motivo para a tela de jogo não abrir.
    return { master: 0.6, music: 0.35 };
  }
}

function clamp(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export class AudioEngine {
  private context: AudioContext | undefined;
  private masterGain: GainNode | undefined;
  private musicGain: GainNode | undefined;
  private music: MusicLoop | undefined;

  private settings = loadSettings();
  /**
   * Último instante em que cada som tocou, para não empilhar cópias.
   *
   * Sem isto, uma onda de fogo que atinge oito tiles dispara oito vezes o mesmo estalo no
   * mesmo milissegundo — o que não soa oito vezes mais forte, soa como um clique estourado.
   */
  private readonly lastPlayed = new Map<Sfx, number>();

  get masterVolume(): number {
    return this.settings.master;
  }

  get musicVolume(): number {
    return this.settings.music;
  }

  setMasterVolume(value: number): void {
    this.settings.master = clamp(value);
    if (this.masterGain && this.context) {
      this.masterGain.gain.setTargetAtTime(this.settings.master, this.context.currentTime, 0.02);
    }
    this.persist();
  }

  setMusicVolume(value: number): void {
    this.settings.music = clamp(value);
    if (this.musicGain && this.context) {
      this.musicGain.gain.setTargetAtTime(this.settings.music, this.context.currentTime, 0.05);
    }
    this.persist();
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      /* bloqueado: o volume vale só nesta sessão */
    }
  }

  /**
   * Garante o contexto. Devolve `undefined` quando o navegador não permite áudio, e a
   * partir daí todo som vira um no-op silencioso — o jogo continua.
   */
  private ensure(): AudioContext | undefined {
    if (this.context) {
      if (this.context.state === 'suspended') void this.context.resume();
      return this.context;
    }

    try {
      const context = new AudioContext();

      const master = context.createGain();
      master.gain.value = this.settings.master;
      master.connect(context.destination);

      const music = context.createGain();
      music.gain.value = this.settings.music;
      music.connect(master);

      this.context = context;
      this.masterGain = master;
      this.musicGain = music;
      return context;
    } catch {
      return undefined;
    }
  }

  /** Toca um efeito. `minGapMs` é a janela em que repetições são engolidas. */
  play(sfx: Sfx, minGapMs = 45): void {
    const context = this.ensure();
    if (!context || !this.masterGain) return;

    const now = context.currentTime * 1000;
    const last = this.lastPlayed.get(sfx) ?? -Infinity;
    if (now - last < minGapMs) return;
    this.lastPlayed.set(sfx, now);

    render(context, this.masterGain, sfx);
  }

  /**
   * Liga a música da região. `caverna` troca a escala e o timbre.
   *
   * Chamada a cada entrada em região; repetir o mesmo ambiente não reinicia nada, que é o
   * que impede o pad de recomeçar do zero a cada travessia de escada.
   */
  setAmbience(cave: boolean): void {
    const context = this.ensure();
    if (!context || !this.musicGain) return;

    if (this.music?.cave === cave) return;

    this.music?.stop();
    this.music = new MusicLoop(context, this.musicGain, cave);
    this.music.start();
  }

  stopMusic(): void {
    this.music?.stop();
    this.music = undefined;
  }
}

// --- Síntese -----------------------------------------------------------------

/**
 * Um som é sempre a mesma receita: uma fonte (oscilador ou ruído), um envelope de ganho e,
 * às vezes, um filtro. O que muda entre eles é a forma da onda, a curva de frequência e o
 * tempo — e é por isso que cabem todos numa função só em vez de numa classe por som.
 */
function render(context: AudioContext, destination: GainNode, sfx: Sfx): void {
  const t = context.currentTime;

  switch (sfx) {
    case Sfx.Step:
      // Passo: um estalo curto de ruído grave. Baixo de propósito — ele toca a cada 150ms
      // enquanto se anda, e qualquer coisa mais presente vira tortura em dois minutos.
      noise(context, destination, t, { duration: 0.06, gain: 0.05, filter: 420, q: 0.7 });
      break;

    case Sfx.Hit:
      // Impacto: ruído com corpo, mais uma queda rápida de tom que dá o "peso".
      noise(context, destination, t, { duration: 0.12, gain: 0.16, filter: 1400, q: 1.2 });
      tone(context, destination, t, {
        type: 'square',
        from: 180,
        to: 60,
        duration: 0.1,
        gain: 0.1,
      });
      break;

    case Sfx.Miss:
      // Erro: só o ar. Sem componente tonal, porque nada foi atingido.
      noise(context, destination, t, { duration: 0.09, gain: 0.07, filter: 2600, q: 0.8 });
      break;

    case Sfx.Death:
      tone(context, destination, t, {
        type: 'sawtooth',
        from: 220,
        to: 40,
        duration: 0.7,
        gain: 0.16,
      });
      noise(context, destination, t + 0.05, { duration: 0.4, gain: 0.08, filter: 700, q: 0.6 });
      break;

    case Sfx.Fire:
      noise(context, destination, t, { duration: 0.5, gain: 0.13, filter: 900, q: 0.5 });
      tone(context, destination, t, {
        type: 'sawtooth',
        from: 90,
        to: 300,
        duration: 0.25,
        gain: 0.07,
      });
      break;

    case Sfx.Ice:
      // Gelo: agudo e cristalino, com dois parciais desafinados que dão o "tinido".
      tone(context, destination, t, { type: 'sine', from: 1400, to: 900, duration: 0.35, gain: 0.09 });
      tone(context, destination, t + 0.02, { type: 'sine', from: 2100, to: 1500, duration: 0.3, gain: 0.05 });
      break;

    case Sfx.Energy:
      tone(context, destination, t, { type: 'square', from: 700, to: 1500, duration: 0.18, gain: 0.08 });
      tone(context, destination, t + 0.06, { type: 'square', from: 1500, to: 400, duration: 0.2, gain: 0.06 });
      break;

    case Sfx.Heal:
      // Cura sobe; é a única família que sobe, e é assim que se lê "isto foi bom".
      tone(context, destination, t, { type: 'sine', from: 440, to: 660, duration: 0.3, gain: 0.09 });
      tone(context, destination, t + 0.08, { type: 'sine', from: 660, to: 880, duration: 0.3, gain: 0.07 });
      break;

    case Sfx.Advance:
      // Arpejo maior: três notas subindo. Ganho é sempre uma frase, nunca um bipe.
      for (const [index, frequency] of [523.25, 659.25, 783.99].entries()) {
        tone(context, destination, t + index * 0.07, {
          type: 'triangle',
          from: frequency,
          to: frequency,
          duration: 0.22,
          gain: 0.09,
        });
      }
      break;

    case Sfx.Loss:
      for (const [index, frequency] of [392, 311.13].entries()) {
        tone(context, destination, t + index * 0.1, {
          type: 'triangle',
          from: frequency,
          to: frequency,
          duration: 0.3,
          gain: 0.1,
        });
      }
      break;

    case Sfx.Coin:
      tone(context, destination, t, { type: 'square', from: 1760, to: 1760, duration: 0.05, gain: 0.05 });
      tone(context, destination, t + 0.05, { type: 'square', from: 2349, to: 2349, duration: 0.07, gain: 0.04 });
      break;

    case Sfx.Ui:
      tone(context, destination, t, { type: 'sine', from: 880, to: 880, duration: 0.04, gain: 0.05 });
      break;
  }
}

interface ToneSpec {
  type: OscillatorType;
  from: number;
  to: number;
  duration: number;
  gain: number;
}

function tone(
  context: AudioContext,
  destination: AudioNode,
  when: number,
  spec: ToneSpec,
): void {
  const oscillator = context.createOscillator();
  oscillator.type = spec.type;
  oscillator.frequency.setValueAtTime(spec.from, when);
  if (spec.to !== spec.from) {
    // Exponencial e não linear: a percepção de altura é logarítmica, e uma rampa linear
    // soa como se acelerasse no fim.
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, spec.to), when + spec.duration);
  }

  const envelope = context.createGain();
  // Ataque de 8ms: cortar em zero produz um estalo audível na borda do buffer.
  envelope.gain.setValueAtTime(0.0001, when);
  envelope.gain.exponentialRampToValueAtTime(spec.gain, when + 0.008);
  envelope.gain.exponentialRampToValueAtTime(0.0001, when + spec.duration);

  oscillator.connect(envelope);
  envelope.connect(destination);
  oscillator.start(when);
  oscillator.stop(when + spec.duration + 0.02);
}

interface NoiseSpec {
  duration: number;
  gain: number;
  /** Corte do passa-baixa. É ele que separa "impacto" de "sopro". */
  filter: number;
  q: number;
}

function noise(
  context: AudioContext,
  destination: AudioNode,
  when: number,
  spec: NoiseSpec,
): void {
  const frames = Math.max(1, Math.floor(context.sampleRate * spec.duration));
  const buffer = context.createBuffer(1, frames, context.sampleRate);
  const data = buffer.getChannelData(0);

  // Ruído branco com decaimento já embutido no buffer: um envelope de ganho sozinho
  // deixaria a cauda audível como um "chiado" antes de fechar.
  for (let i = 0; i < frames; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / frames) ** 2;
  }

  const source = context.createBufferSource();
  source.buffer = buffer;

  const filter = context.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = spec.filter;
  filter.Q.value = spec.q;

  const envelope = context.createGain();
  envelope.gain.value = spec.gain;

  source.connect(filter);
  filter.connect(envelope);
  envelope.connect(destination);
  source.start(when);
}

// --- Música ------------------------------------------------------------------

/**
 * Pad generativo, sem loop gravado.
 *
 * Duas notas longas de cada vez, sorteadas de uma escala, trocadas a cada compasso. Não é
 * sofisticação: é o que uma trilha de fundo precisa fazer — existir sem chamar atenção e
 * **não se repetir de forma reconhecível**, que é exatamente o defeito de um loop curto de
 * áudio gravado. Uma pessoa nota a repetição de um arquivo de trinta segundos na terceira
 * volta; não nota a de um gerador.
 *
 * A escala muda entre superfície e caverna, e é o único parâmetro que muda: menor natural
 * lá embaixo, maior com sexta lá em cima. Não é teoria musical elaborada — é o mínimo para
 * que os dois lugares soem diferentes sem exigir duas trilhas.
 */
class MusicLoop {
  readonly cave: boolean;

  private readonly context: AudioContext;
  private readonly destination: AudioNode;
  private timer: ReturnType<typeof setInterval> | undefined;

  constructor(context: AudioContext, destination: AudioNode, cave: boolean) {
    this.context = context;
    this.destination = destination;
    this.cave = cave;
  }

  private get scale(): number[] {
    // Lá menor lá embaixo; dó maior com sexta lá em cima.
    return this.cave
      ? [110, 130.81, 146.83, 164.81, 196, 220]
      : [130.81, 146.83, 164.81, 196, 220, 261.63];
  }

  start(): void {
    this.chord();
    // Compasso de 6 segundos: longo o bastante para o pad soar como fundo e não como
    // sequência, curto o bastante para o lugar não parecer congelado.
    this.timer = setInterval(() => this.chord(), 6000);
  }

  private chord(): void {
    const now = this.context.currentTime;
    const scale = this.scale;
    const root = scale[Math.floor(Math.random() * scale.length)] ?? scale[0]!;
    const fifth = root * 1.5;

    for (const [index, frequency] of [root, fifth].entries()) {
      const oscillator = this.context.createOscillator();
      oscillator.type = 'triangle';
      oscillator.frequency.value = frequency;

      // Detune leve entre as duas vozes: sem ele o intervalo soa como um bipe de teste.
      oscillator.detune.value = index === 0 ? -4 : 5;

      const envelope = this.context.createGain();
      envelope.gain.setValueAtTime(0.0001, now);
      envelope.gain.exponentialRampToValueAtTime(0.06, now + 1.5);
      envelope.gain.exponentialRampToValueAtTime(0.0001, now + 6);

      const filter = this.context.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = this.cave ? 500 : 900;

      oscillator.connect(filter);
      filter.connect(envelope);
      envelope.connect(this.destination);
      oscillator.start(now);
      oscillator.stop(now + 6.2);
    }
  }

  stop(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
  }
}
